/**
 * Items lying on the ground: the bounce, the pickup radius, the rot.
 *
 * The server owns what fell and whether a claim succeeds (DropsService). This is
 * purely how it looks and when it gets collected: a drop lands with a little hop,
 * sits there, and is swept up when the hero walks within a tile — or fades and is
 * gone after its TTL. Picking up POSTs the claim; the server credits the bag or the
 * farm's resource columns.
 *
 * Icons are the same 16px item art the bag uses (`/sprites/items`, `/sprites/resources`).
 */
import { Container, Sprite, Text, Texture } from 'pixi.js';
import { CANVAS_FONT } from '../fonts';
import { TILE } from './world';

export interface GroundDropData {
  id: string;
  tx: number;
  ty: number;
  itemId: string;
  quantity: number;
  source?: string;
  expiresInMs: number;
}

interface DropEntity {
  data: GroundDropData;
  sprite: Sprite;
  badge: Container | null;
  x: number;
  y: number;
  /** Landing hop. */
  vy: number;
  groundY: number;
  bornAt: number;
  ttlMs: number;
  claiming: boolean;
}

const PICKUP_TILES = 1;      // walk this close and it's yours
const HOP = -120;            // initial upward velocity of the landing bounce (px/s)
const GRAVITY = 620;
const FADE_MS = 1200;        // start fading this long before expiry

export interface Drops {
  layer: Container;
  /** Reconcile with the server's list — add newcomers, forget the gone. */
  sync(list: GroundDropData[]): void;
  /**
   * Per frame. Returns the ids now within pickup range of (px,py) that aren't
   * already being claimed — the caller POSTs the claim and, on success, calls
   * `remove`. `now` is passed so the ticker stays deterministic in tests.
   */
  update(dt: number, px: number, py: number, now: number): string[];
  remove(id: string): void;
  /**
   * Put a drop back in play after a claim that never landed (offline, 429, a
   * failed batch). Without this the entity stays flagged `claiming` for the rest
   * of the session and the player can never pick it up again.
   */
  release(id: string): void;
  has(id: string): boolean;
  destroy(): void;
}

export function createDrops(
  iconFor: (itemId: string) => Texture,
  /** Swaps the icon in once it has loaded — mob/animal loot is not preloaded. */
  applyIcon?: (s: Sprite, itemId: string) => void,
): Drops {
  const layer = new Container();
  layer.eventMode = 'none';
  layer.sortableChildren = true;
  const entities = new Map<string, DropEntity>();

  const PX = TILE / 16; // item icons are 16px

  const spawn = (d: GroundDropData, now: number) => {
    const sprite = new Sprite(iconFor(d.itemId));
    applyIcon?.(sprite, d.itemId);
    sprite.eventMode = 'none';   // picked up by walking over it, never by tapping
    sprite.anchor.set(0.5, 1);
    sprite.scale.set(PX * 0.8);
    const groundY = (d.ty + 0.9) * TILE;
    sprite.x = (d.tx + 0.5) * TILE;
    sprite.y = groundY;
    sprite.zIndex = groundY;
    layer.addChild(sprite);

    let badge: Container | null = null;
    if (d.quantity > 1) {
      // A tiny count so a stack of 3 doesn't read as one.
      //
      // The NUMBER was missing. This drew the dark backing plate and stopped, so a
      // stacked drop — which is most of what a mob leaves, bones and flesh come in
      // twos — landed next to a solid black rectangle and nothing else. It read as
      // the drop itself being a black square, because next to a 16px icon a 12x10
      // black box IS the thing your eye lands on.
      const b = new Container();
      b.zIndex = groundY + 1;
      const count = new Text({
        text: `x${d.quantity}`,
        style: { fontFamily: CANVAS_FONT, fontSize: 10, fill: 0xffe9b8, stroke: { color: 0x1c150c, width: 3 } },
      });
      // The plate is sized to the text rather than to a guessed 12x10: "x2" and
      // "x12" are different widths, and a fixed plate clips one or floats around
      // the other.
      const plate = new Sprite(Texture.WHITE);
      plate.tint = 0x000000; plate.alpha = 0.55;
      plate.width = count.width + 4; plate.height = count.height + 2;
      count.x = 2; count.y = 1;
      b.addChild(plate, count);
      layer.addChild(b);
      badge = b;
    }
    entities.set(d.id, {
      data: d, sprite, badge,
      x: sprite.x, y: groundY - 8, vy: HOP, groundY,
      bornAt: now, ttlMs: d.expiresInMs, claiming: false,
    });
  };

  return {
    layer,
    sync(list) {
      const seen = new Set(list.map((d) => d.id));
      // Drop anything the server no longer lists (claimed or expired elsewhere).
      for (const [id, e] of entities) {
        if (!seen.has(id)) { e.sprite.destroy(); e.badge?.destroy(); entities.delete(id); }
      }
      for (const d of list) if (!entities.has(d.id)) spawn(d, performance.now());
    },
    update(dt, px, py, now) {
      const ready: string[] = [];
      for (const [id, e] of entities) {
        // Landing hop, then rest on the ground.
        if (e.y < e.groundY || e.vy < 0) {
          e.vy += GRAVITY * dt;
          e.y = Math.min(e.groundY, e.y + e.vy * dt);
          if (e.y >= e.groundY) { e.y = e.groundY; e.vy = 0; }
        }
        e.sprite.y = e.y;
        e.sprite.zIndex = e.groundY;
        if (e.badge) { e.badge.x = e.x + 4; e.badge.y = e.y - 12; }

        // Fade out as it nears expiry.
        const ageMs = now - e.bornAt;
        const leftMs = e.ttlMs - ageMs;
        // The badge fades WITH the item. Fading only the sprite left the count
        // plate at full opacity over a vanishing drop, so the last thing on screen
        // before it expired was, again, a floating dark rectangle.
        e.sprite.alpha = leftMs < FADE_MS ? Math.max(0, leftMs / FADE_MS) : 1;
        if (e.badge) e.badge.alpha = e.sprite.alpha;
        if (leftMs <= 0) { e.sprite.destroy(); e.badge?.destroy(); entities.delete(id); continue; }

        // Close enough to sweep up?
        if (!e.claiming) {
          const dist = Math.hypot(px - e.x, py - e.groundY) / TILE;
          if (dist <= PICKUP_TILES) { e.claiming = true; ready.push(id); }
        }
      }
      return ready;
    },
    remove(id) {
      const e = entities.get(id);
      if (e) { e.sprite.destroy(); e.badge?.destroy(); entities.delete(id); }
    },
    release(id) {
      const e = entities.get(id);
      if (e) e.claiming = false;
    },
    has(id) { return entities.has(id); },
    destroy() {
      layer.destroy({ children: true });
      entities.clear();
    },
  };
}
